import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Search, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getPrescriptionQueue,
  PrescriptionQueueRow,
} from '../../services/MasterAdmin/prescriptionQueueService';
import {
  getAmbulanceQueue,
  AmbulanceQueueRow,
} from '../../services/MasterAdmin/ambulanceQueueService';
import {
  getInstantConsultationQueue,
  InstantConsultationQueueRow,
} from '../../services/MasterAdmin/instantConsultationQueueService';

type QueueTab = 'medicine' | 'ambulance' | 'instant-consultation';

type VendorDialogVendor = {
  name: string;
  vendorId: string | null;
  kind: string;
  pincode?: string | null;
  inviteStatus?: string | null;
  inviteMode?: string | null;
};

const statusBadgeClass: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  assigned: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  ongoing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

function getShortCode(id: string): string {
  const parts = String(id || '').split('-').filter(Boolean);
  const lastPart = parts.length ? parts[parts.length - 1] : String(id || '');
  return lastPart.toUpperCase();
}

function formatAmbulanceType(type: string): string {
  return String(type || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPinList(pincodes: string[] | undefined | null): string {
  const list = (pincodes || []).filter(Boolean);
  const unique = list.filter((pin, index) => list.indexOf(pin) === index);
  return unique.length ? unique.join(', ') : '—';
}

function formatVendorNames(names: string[] | undefined | null, max = 2): string {
  const list = (names || []).filter(Boolean);
  if (!list.length) return '—';
  if (list.length <= max) return list.join(', ');
  return `${list.slice(0, max).join(', ')} +${list.length - max} more`;
}

const AMBULANCE_ONGOING_STATUSES = new Set([
  'ACCEPTED',
  'PAYMENT_DONE',
  'AMBULANCE_LEFT',
  'ARRIVED',
  'STARTED',
]);

function isAmbulanceOngoing(row: AmbulanceQueueRow): boolean {
  return AMBULANCE_ONGOING_STATUSES.has(String(row.bookingStatus || '').toUpperCase());
}

const COMPLETED_STATUS_KEYWORDS = ['complete', 'deliver', 'closed'];

function isCancelledQueueStatus(queueStatus: string, ...statuses: unknown[]): boolean {
  if (String(queueStatus || '').toLowerCase() === 'cancelled') return true;
  return statuses.some((raw) => {
    const value = String(raw || '').toLowerCase();
    return value.includes('cancel') || value.includes('reject') || value.includes('expired');
  });
}

function shouldShowInQueue(queueStatus: string, ...statuses: unknown[]): boolean {
  if (isCancelledQueueStatus(queueStatus, ...statuses)) return true;
  return !statuses.some((raw) => {
    const value = String(raw || '').toLowerCase();
    if (!value) return false;
    return COMPLETED_STATUS_KEYWORDS.some((keyword) => value.includes(keyword));
  });
}

function isMedicineQueueVisible(row: PrescriptionQueueRow): boolean {
  return shouldShowInQueue(row.queueStatus, row.status);
}

function isAmbulanceQueueVisible(row: AmbulanceQueueRow): boolean {
  return shouldShowInQueue(row.queueStatus, row.bookingStatus);
}

function isInstantConsultationQueueVisible(row: InstantConsultationQueueRow): boolean {
  return shouldShowInQueue(row.queueStatus, row.status);
}

const INSTANT_WITHDRAWN_OR_CANCELLED_STATUSES = new Set(['cancelled', 'expired', 'no_call', 'no_show']);

function getInstantConsultationDisplayAmount(row: InstantConsultationQueueRow): number {
  const paidAmount = Number(row.paidAmount || 0);
  if (paidAmount > 0) return paidAmount;
  return Number(row.acceptedInstantFee || 0);
}

function shouldShowInstantConsultationAmount(row: InstantConsultationQueueRow): boolean {
  const queueStatus = String(row.queueStatus || '').toLowerCase();
  const status = String(row.status || '').toLowerCase();
  const paymentStatus = String(row.paymentStatus || '').toLowerCase();
  if (queueStatus === 'cancelled' || INSTANT_WITHDRAWN_OR_CANCELLED_STATUSES.has(status)) {
    return false;
  }
  if (paymentStatus !== 'paid') return false;
  return getInstantConsultationDisplayAmount(row) > 0;
}

function formatCancelledByLabel(
  cancelledBy: string | null | undefined,
  service: 'medicine' | 'ambulance' | 'instant-consultation'
): string | null {
  if (!cancelledBy) return null;
  const value = String(cancelledBy).toLowerCase();
  if (value === 'user') return 'User';
  if (service === 'ambulance') {
    if (value === 'agency') return 'Vendor';
    if (value === 'system') return 'System';
  }
  if (service === 'instant-consultation') {
    if (value === 'doctor') return 'Vendor';
    if (value === 'admin') return 'Admin';
  }
  if (value === 'vendor') return 'Vendor';
  return cancelledBy;
}

const PrescriptionQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedPrescriptionId = searchParams.get('prescriptionId');
  const highlightedBookingId = searchParams.get('bookingId');
  const highlightedAppointmentId = searchParams.get('appointmentId');
  const tabParam = searchParams.get('tab');
  const initialTab: QueueTab =
    tabParam === 'instant-consultation' || highlightedAppointmentId
      ? 'instant-consultation'
      : tabParam === 'ambulance' || highlightedBookingId
        ? 'ambulance'
        : 'medicine';

  const [activeTab, setActiveTab] = useState<QueueTab>(initialTab);
  const [medicineRows, setMedicineRows] = useState<PrescriptionQueueRow[]>([]);
  const [ambulanceRows, setAmbulanceRows] = useState<AmbulanceQueueRow[]>([]);
  const [instantRows, setInstantRows] = useState<InstantConsultationQueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [vendorDialog, setVendorDialog] = useState<{
    open: boolean;
    refLabel: string;
    refId: string;
    vendors: VendorDialogVendor[];
  }>({ open: false, refLabel: '', refId: '', vendors: [] });

  const fetchMedicineQueue = useCallback(async () => {
    const response = await getPrescriptionQueue({ status: 'all', limit: 100 });
    if (response.success) {
      setMedicineRows(response.data.queue || []);
    } else {
      throw new Error('Failed to load medicine queue');
    }
  }, []);

  const fetchAmbulanceQueue = useCallback(async () => {
    const response = await getAmbulanceQueue({ status: 'all', limit: 100 });
    if (response.success) {
      setAmbulanceRows(response.data.queue || []);
    } else {
      throw new Error('Failed to load ambulance queue');
    }
  }, []);

  const fetchInstantConsultationQueue = useCallback(async () => {
    const response = await getInstantConsultationQueue({ status: 'all', limit: 100 });
    if (response.success) {
      setInstantRows(response.data.queue || []);
    } else {
      throw new Error('Failed to load instant consultation queue');
    }
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        fetchMedicineQueue(),
        fetchAmbulanceQueue(),
        fetchInstantConsultationQueue(),
      ]);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Failed to load request queue');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [fetchMedicineQueue, fetchAmbulanceQueue, fetchInstantConsultationQueue]);

  useEffect(() => {
    void fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    if (tabParam === 'instant-consultation' || highlightedAppointmentId) {
      setActiveTab('instant-consultation');
    } else if (tabParam === 'ambulance' || highlightedBookingId) {
      setActiveTab('ambulance');
    } else if (tabParam === 'medicine' || highlightedPrescriptionId) {
      setActiveTab('medicine');
    }
  }, [tabParam, highlightedAppointmentId, highlightedBookingId, highlightedPrescriptionId]);

  const switchTab = (tab: QueueTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const filteredMedicineRows = useMemo(() => {
    const visibleRows = medicineRows.filter(isMedicineQueueVisible);
    const q = searchText.trim().toLowerCase();
    if (!q) return visibleRows;
    return visibleRows.filter((row) =>
      [
        row.prescriptionId,
        row.userId,
        row.userName || '',
        row.userPhone || '',
        row.deliveryAddress || '',
        row.deliveryPincode || '',
        ...(row.requestedVendorNames || []),
        ...(row.requestedVendorPincodes || []),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [medicineRows, searchText]);

  const filteredAmbulanceRows = useMemo(() => {
    const visibleRows = ambulanceRows.filter(isAmbulanceQueueVisible);
    const q = searchText.trim().toLowerCase();
    if (!q) return visibleRows;
    return visibleRows.filter((row) =>
      [
        row.bookingId,
        row.userId || '',
        row.userName || '',
        row.userPhone || '',
        row.patientName || '',
        row.patientPhone || '',
        row.customAgencyName || '',
        row.agencyDisplayName || '',
        row.bookingSource || '',
        row.pickupAddress || '',
        row.dropAddress || '',
        ...(row.routeStops || []).map((s) => s.address || ''),
        ...(row.requestedVendorNames || []),
        ...(row.requestedVendors || []).map((v) => v.inviteStatus || ''),
        ...(row.bookingInvites || []).map((i) => i.vendorName),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [ambulanceRows, searchText]);

  const medicineTabCount = useMemo(
    () => medicineRows.filter(isMedicineQueueVisible).length,
    [medicineRows]
  );
  const ambulanceTabCount = useMemo(
    () => ambulanceRows.filter(isAmbulanceQueueVisible).length,
    [ambulanceRows]
  );
  const instantTabCount = useMemo(
    () => instantRows.filter(isInstantConsultationQueueVisible).length,
    [instantRows]
  );

  const filteredInstantRows = useMemo(() => {
    const visibleRows = instantRows.filter(isInstantConsultationQueueVisible);
    const q = searchText.trim().toLowerCase();
    if (!q) return visibleRows;
    return visibleRows.filter((row) =>
      [
        row.clinicAppointmentId,
        row.userId,
        row.userName || '',
        row.userPhone || '',
        row.selectedSpeciality || '',
        ...(row.requestedVendorNames || []),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [instantRows, searchText]);

  const searchPlaceholder =
    activeTab === 'medicine'
      ? 'Search ID, user, phone, address, pincode, vendor...'
      : activeTab === 'ambulance'
        ? 'Search booking, user, locations, pincodes, agencies...'
        : 'Search appointment, user, phone, speciality, doctor...';

  return (
    <>
      <div className="w-full min-w-0 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            Monitor service flow across medicine, ambulance, and instant consultation requests.
            Cancelled requests are included and show who cancelled (user or vendor). Use Track on map for ongoing ambulance bookings.
          </p>
          <button
            type="button"
            onClick={() => void fetchQueue()}
            disabled={loading}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700 sm:gap-2">
          <button
            type="button"
            onClick={() => switchTab('medicine')}
            className={`shrink-0 px-3 py-2 text-sm font-medium border-b-2 -mb-px sm:px-4 ${
              activeTab === 'medicine'
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Medicine ({medicineTabCount})
          </button>
          <button
            type="button"
            onClick={() => switchTab('ambulance')}
            className={`shrink-0 px-3 py-2 text-sm font-medium border-b-2 -mb-px sm:px-4 ${
              activeTab === 'ambulance'
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Ambulance ({ambulanceTabCount})
          </button>
          <button
            type="button"
            onClick={() => switchTab('instant-consultation')}
            className={`shrink-0 px-3 py-2 text-sm font-medium border-b-2 -mb-px sm:px-4 ${
              activeTab === 'instant-consultation'
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Instant consultation ({instantTabCount})
          </button>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800 dark:shadow-gray-900/30">
          <div className="border-b p-4 dark:border-gray-700">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:w-96"
                placeholder={searchPlaceholder}
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => setSearchText('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Mobile card list */}
          <div className="space-y-3 p-4 md:hidden">
            {loading ? (
              <QueueMobileMessage message="Loading queue..." loading />
            ) : error ? (
              <QueueMobileMessage message={error} error />
            ) : activeTab === 'medicine' ? (
              filteredMedicineRows.length === 0 ? (
                <QueueMobileMessage
                  message={
                    searchText
                      ? 'No medicine queue record matches your search.'
                      : 'No medicine requests in queue.'
                  }
                />
              ) : (
                filteredMedicineRows.map((row) => (
                  <MedicineQueueCard
                    key={row.prescriptionId}
                    row={row}
                    highlighted={highlightedPrescriptionId === row.prescriptionId}
                    onNavigate={navigate}
                    onViewVendors={(vendors) =>
                      setVendorDialog({
                        open: true,
                        refLabel: 'Prescription',
                        refId: row.prescriptionId,
                        vendors,
                      })
                    }
                  />
                ))
              )
            ) : activeTab === 'ambulance' ? (
              filteredAmbulanceRows.length === 0 ? (
                <QueueMobileMessage
                  message={
                    searchText
                      ? 'No ambulance queue record matches your search.'
                      : 'No ambulance requests in queue.'
                  }
                />
              ) : (
                filteredAmbulanceRows.map((row) => (
                  <AmbulanceQueueCard
                    key={row.bookingId}
                    row={row}
                    highlighted={highlightedBookingId === row.bookingId}
                    onNavigate={navigate}
                    onViewVendors={(vendors) =>
                      setVendorDialog({
                        open: true,
                        refLabel: 'Booking',
                        refId: row.bookingId,
                        vendors,
                      })
                    }
                  />
                ))
              )
            ) : filteredInstantRows.length === 0 ? (
              <QueueMobileMessage
                message={
                  searchText
                    ? 'No instant consultation record matches your search.'
                    : 'No instant consultation requests in queue.'
                }
              />
            ) : (
              filteredInstantRows.map((row) => (
                <InstantConsultationQueueCard
                  key={row.clinicAppointmentId}
                  row={row}
                  highlighted={highlightedAppointmentId === row.clinicAppointmentId}
                  onNavigate={navigate}
                  onViewVendors={(vendors) =>
                    setVendorDialog({
                      open: true,
                      refLabel: 'Appointment',
                      refId: row.clinicAppointmentId,
                      vendors,
                    })
                  }
                />
              ))
            )}
          </div>

          {/* Desktop table */}
          <div className="ma-table-scroll scrollbar-light hidden md:block">
            {activeTab === 'medicine' ? (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Prescription
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      User
                    </th>
                    <th className="min-w-[12rem] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Delivery location
                    </th>
                    <th className="min-w-[10rem] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Service providers
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Reminders
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Requested at
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <QueueLoadingRow colSpan={7} />
                  ) : error ? (
                    <QueueErrorRow colSpan={7} message={error} />
                  ) : filteredMedicineRows.length === 0 ? (
                    <QueueEmptyRow
                      colSpan={7}
                      message={
                        searchText
                          ? 'No medicine queue record matches your search.'
                          : 'No medicine requests in queue.'
                      }
                    />
                  ) : (
                    filteredMedicineRows.map((row) => {
                      const highlighted = highlightedPrescriptionId === row.prescriptionId;
                      return (
                        <tr
                          key={row.prescriptionId}
                          className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                            highlighted ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white lg:px-6">
                            <div className="font-medium" title={row.prescriptionId}>
                              {getShortCode(row.prescriptionId)}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm lg:px-6">
                            <UserDetailsCell
                              userId={row.userId}
                              name={row.userName}
                              phone={row.userPhone}
                              onNavigate={navigate}
                            />
                          </td>
                          <td className="max-w-xs px-4 py-4 text-sm lg:px-6">
                            <LocationCell
                              address={row.deliveryAddress}
                              pincode={row.deliveryPincode}
                              pinLabel="Delivery PIN"
                            />
                          </td>
                          <td className="px-4 py-4 text-sm lg:px-6">
                            <ServiceProvidersCell
                              names={row.requestedVendorNames}
                              pincodes={row.requestedVendorPincodes}
                              count={row.vendorsNotified}
                              vendors={row.requestedVendors}
                              onViewAll={() =>
                                setVendorDialog({
                                  open: true,
                                  refLabel: 'Prescription',
                                  refId: row.prescriptionId,
                                  vendors: row.requestedVendors || [],
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 lg:px-6">
                            <StatusBadge status={row.queueStatus} />
                            <CancellationDetails
                              queueStatus={row.queueStatus}
                              cancelledBy={formatCancelledByLabel(row.cancelledBy, 'medicine')}
                              cancellationReason={row.cancellationReason}
                              detailStatus={row.status}
                            />
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500 dark:text-gray-400 lg:px-6">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {row.reminderSentCount ?? 0}
                            </div>
                            <div className="text-xs">Active: {row.activeReminderCount ?? 0}</div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500 dark:text-gray-400 lg:px-6">
                            {new Date(row.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            ) : activeTab === 'ambulance' ? (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Booking
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      User
                    </th>
                    <th className="min-w-[14rem] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Pickup / drop
                    </th>
                    <th className="min-w-[10rem] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Service providers
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Requested at
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <QueueLoadingRow colSpan={8} />
                  ) : error ? (
                    <QueueErrorRow colSpan={8} message={error} />
                  ) : filteredAmbulanceRows.length === 0 ? (
                    <QueueEmptyRow
                      colSpan={8}
                      message={
                        searchText
                          ? 'No ambulance queue record matches your search.'
                          : 'No ambulance requests in queue.'
                      }
                    />
                  ) : (
                    filteredAmbulanceRows.map((row) => {
                      const highlighted = highlightedBookingId === row.bookingId;
                      const bookingHref =
                        row.acceptedByVendorId
                          ? `/vendors/ambulance/${row.acceptedByVendorId}/booking/${row.bookingId}`
                          : row.requestedVendors[0]?.vendorId
                            ? `/vendors/ambulance/${row.requestedVendors[0].vendorId}/booking/${row.bookingId}`
                            : null;
                      return (
                        <tr
                          key={row.bookingId}
                          className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                            highlighted ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white lg:px-6">
                            {bookingHref ? (
                              <button
                                type="button"
                                onClick={() => navigate(bookingHref)}
                                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                                title={row.bookingId}
                              >
                                {getShortCode(row.bookingId)}
                              </button>
                            ) : (
                              <div className="font-medium" title={row.bookingId}>
                                {getShortCode(row.bookingId)}
                              </div>
                            )}
                            {row.bookingSource === 'MASTER_ADMIN' ? (
                              <span className="mt-1 inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                Manual
                              </span>
                            ) : null}
                          </td>
                          <td className="px-4 py-4 text-sm lg:px-6">
                            {row.userId ? (
                              <UserDetailsCell
                                userId={row.userId}
                                name={row.userName}
                                phone={row.userPhone}
                                onNavigate={navigate}
                              />
                            ) : (
                              <div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {row.patientName || '—'}
                                </span>
                                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                  {row.patientPhone || '—'}
                                </div>
                              </div>
                            )}
                            {row.userId && (row.patientName || row.patientPhone) ? (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Patient: {row.patientName || '—'}
                                {row.patientPhone ? ` · ${row.patientPhone}` : ''}
                              </div>
                            ) : null}
                          </td>
                          <td className="max-w-sm px-4 py-4 text-sm lg:px-6">
                            <AmbulanceRouteCell row={row} />
                          </td>
                          <td className="px-4 py-4 text-sm lg:px-6">
                            <AmbulanceServiceProvidersCell
                              vendors={row.requestedVendors}
                              count={row.vendorsNotified}
                              onViewAll={() =>
                                setVendorDialog({
                                  open: true,
                                  refLabel: 'Booking',
                                  refId: row.bookingId,
                                  vendors: (row.requestedVendors || []).map((v) => ({
                                    vendorId: v.vendorId,
                                    name: v.name,
                                    kind: v.kind,
                                    inviteStatus: v.inviteStatus,
                                    inviteMode: v.inviteMode,
                                  })),
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300 lg:px-6">
                            <div>{formatAmbulanceType(row.ambulanceType)}</div>
                            <div className="text-xs text-gray-500">
                              {row.pickupTimeType === 'SCHEDULED' ? 'Scheduled' : 'Instant'} ·{' '}
                              {row.dispatchMode === 'BROADCAST' ? 'Broadcast' : 'Direct'}
                            </div>
                            {row.bookingSource === 'MASTER_ADMIN' && Number(row.totalPayableAmount) > 0 ? (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                ₹{Number(row.totalPayableAmount).toLocaleString('en-IN')}
                                {row.paymentCollectionType === 'ADVANCE' && Number(row.remainingAmount) > 0
                                  ? ` · rem. ₹${Number(row.remainingAmount).toLocaleString('en-IN')}`
                                  : ''}
                              </div>
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 lg:px-6">
                            <StatusBadge status={row.queueStatus} />
                            <CancellationDetails
                              queueStatus={row.queueStatus}
                              cancelledBy={formatCancelledByLabel(row.cancelledBy, 'ambulance')}
                              cancellationReason={row.cancellationReason}
                              detailStatus={row.bookingStatus}
                            />
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500 dark:text-gray-400 lg:px-6">
                            {new Date(row.createdAt).toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 lg:px-6">
                            {isAmbulanceOngoing(row) ? (
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/request-queue/ambulance/${encodeURIComponent(row.bookingId)}/track`,
                                    { state: { row } }
                                  )
                                }
                                className="inline-flex rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400"
                              >
                                Track on map
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Appointment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Speciality
                    </th>
                    <th className="min-w-[10rem] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Doctors notified
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Requested at
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <QueueLoadingRow colSpan={7} />
                  ) : error ? (
                    <QueueErrorRow colSpan={7} message={error} />
                  ) : filteredInstantRows.length === 0 ? (
                    <QueueEmptyRow
                      colSpan={7}
                      message={
                        searchText
                          ? 'No instant consultation record matches your search.'
                          : 'No instant consultation requests in queue.'
                      }
                    />
                  ) : (
                    filteredInstantRows.map((row) => {
                      const highlighted = highlightedAppointmentId === row.clinicAppointmentId;
                      const shouldShowAmount = shouldShowInstantConsultationAmount(row);
                      const displayAmount = shouldShowAmount
                        ? getInstantConsultationDisplayAmount(row)
                        : 0;
                      return (
                        <tr
                          key={row.clinicAppointmentId}
                          className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                            highlighted ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white lg:px-6">
                            <div className="font-medium" title={row.clinicAppointmentId}>
                              {getShortCode(row.clinicAppointmentId)}
                            </div>
                            {row.expiresAt ? (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Expires: {new Date(row.expiresAt).toLocaleString()}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-4 text-sm lg:px-6">
                            <UserDetailsCell
                              userId={row.userId}
                              name={row.userName}
                              phone={row.userPhone}
                              onNavigate={navigate}
                            />
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300 lg:px-6">
                            {row.selectedSpeciality || '—'}
                          </td>
                          <td className="px-4 py-4 text-sm lg:px-6">
                            <AmbulanceServiceProvidersCell
                              vendors={row.requestedVendors}
                              count={row.vendorsNotified}
                              onViewAll={() =>
                                setVendorDialog({
                                  open: true,
                                  refLabel: 'Appointment',
                                  refId: row.clinicAppointmentId,
                                  vendors: (row.requestedVendors || []).map((v) => ({
                                    vendorId: v.vendorId,
                                    name: v.name,
                                    kind: v.kind,
                                    inviteStatus: v.inviteStatus,
                                    inviteMode: v.inviteMode,
                                  })),
                                })
                              }
                            />
                            {row.acceptedByVendorName ? (
                              <div className="mt-1 text-xs text-green-700 dark:text-green-400">
                                Accepted by {row.acceptedByVendorName}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300 lg:px-6">
                            <div>{row.paymentStatus || '—'}</div>
                            {shouldShowAmount ? (
                              <div className="text-xs text-gray-500">₹{displayAmount}</div>
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 lg:px-6">
                            <StatusBadge status={row.queueStatus} />
                            <CancellationDetails
                              queueStatus={row.queueStatus}
                              cancelledBy={formatCancelledByLabel(row.cancelledBy, 'instant-consultation')}
                              cancellationReason={row.cancellationReason}
                              detailStatus={row.status}
                            />
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500 dark:text-gray-400 lg:px-6">
                            {new Date(row.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {vendorDialog.open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-2 sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-xl bg-white shadow-xl dark:bg-gray-800 sm:rounded-lg">
            <div className="flex items-center justify-between border-b p-4 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Requested vendors</h3>
              <button
                type="button"
                onClick={() => setVendorDialog({ open: false, refLabel: '', refId: '', vendors: [] })}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                {vendorDialog.refLabel}: {vendorDialog.refId}
              </p>
              <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
                {vendorDialog.vendors.map((vendor, idx) => (
                  <li key={`${vendor.vendorId || vendor.name}-${idx}`}>
                    {vendor.vendorId ? (
                      <button
                        type="button"
                        onClick={() => {
                          navigate(`/vendors/${vendor.kind}/${vendor.vendorId}`);
                          setVendorDialog({ open: false, refLabel: '', refId: '', vendors: [] });
                        }}
                        className="w-full rounded-md bg-gray-50 px-3 py-2 text-left text-sm text-primary-700 hover:bg-primary-50 dark:bg-gray-700/50 dark:text-primary-300 dark:hover:bg-primary-900/30"
                      >
                        <span className="font-medium">{vendor.name}</span>
                        {vendor.pincode ? (
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                            PIN: {vendor.pincode}
                          </span>
                        ) : null}
                        {vendor.inviteStatus ? (
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                            {vendor.inviteStatus}
                            {vendor.inviteMode ? ` · ${vendor.inviteMode}` : ''}
                          </span>
                        ) : null}
                      </button>
                    ) : (
                      <div className="w-full rounded-md bg-gray-50 px-3 py-2 text-left text-sm dark:bg-gray-700/50">
                        <span className="font-medium text-gray-900 dark:text-white">{vendor.name}</span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                          Custom agency
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold leading-5 ${
        statusBadgeClass[status] || 'bg-gray-100 text-gray-700'
      }`}
    >
      {status}
    </span>
  );
}

function CancellationDetails({
  queueStatus,
  cancelledBy,
  cancellationReason,
  detailStatus,
}: {
  queueStatus: string;
  cancelledBy: string | null;
  cancellationReason?: string | null;
  detailStatus?: string | null;
}) {
  const isCancelled = String(queueStatus || '').toLowerCase() === 'cancelled';
  if (!isCancelled && !cancelledBy) {
    return detailStatus ? (
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{detailStatus}</div>
    ) : null;
  }

  return (
    <div className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
      {detailStatus ? <div>{detailStatus}</div> : null}
      {cancelledBy ? (
        <div className="font-medium text-red-700 dark:text-red-300">Cancelled by {cancelledBy}</div>
      ) : null}
      {cancellationReason ? (
        <div className="line-clamp-2" title={cancellationReason}>
          Reason: {cancellationReason}
        </div>
      ) : null}
    </div>
  );
}

function UserDetailsCell({
  userId,
  name,
  phone,
  onNavigate,
}: {
  userId: string | null;
  name: string | null;
  phone: string | null;
  onNavigate: (path: string) => void;
}) {
  const displayName = name || userId || '—';
  return (
    <div>
      {userId ? (
        <button
          type="button"
          onClick={() => onNavigate(`/users/${userId}`)}
          className="font-medium text-primary-600 hover:underline dark:text-primary-400"
          title={userId}
        >
          {displayName}
        </button>
      ) : (
        <span className="font-medium text-gray-900 dark:text-white">{displayName}</span>
      )}
      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{phone || '—'}</div>
    </div>
  );
}

function LocationCell({
  address,
  pincode,
  pinLabel = 'PIN',
}: {
  address: string | null;
  pincode: string | null;
  pinLabel?: string;
}) {
  if (!address && !pincode) return <span className="text-gray-400">—</span>;
  return (
    <div className="space-y-1">
      {address ? (
        <p className="line-clamp-2 text-gray-900 dark:text-white" title={address}>
          {address}
        </p>
      ) : null}
      {pincode ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {pinLabel}: {pincode}
        </p>
      ) : null}
    </div>
  );
}

function AmbulanceRouteCell({ row }: { row: AmbulanceQueueRow }) {
  const stops = Array.isArray(row.routeStops) ? row.routeStops.filter((s) => s?.address) : [];
  if (stops.length >= 2) {
    return (
      <div className="space-y-1.5 text-sm">
        {stops.map((stop, index) => (
          <div key={`${stop.label || index}-${stop.address}`}>
            <p className="text-xs font-medium uppercase text-gray-400">
              {stop.label || String.fromCharCode(65 + index)}
              {index === 0 ? ' · Pickup' : index === stops.length - 1 ? ' · Drop' : ''}
            </p>
            <p className="line-clamp-2 text-gray-900 dark:text-white" title={stop.address}>
              {stop.address}
            </p>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-2 text-sm">
      <div>
        <p className="text-xs font-medium uppercase text-gray-400">Pickup</p>
        <p className="line-clamp-2 text-gray-900 dark:text-white" title={row.pickupAddress}>
          {row.pickupAddress || '—'}
        </p>
      </div>
      <div>
        <p className="text-xs font-medium uppercase text-gray-400">Drop</p>
        <p className="line-clamp-2 text-gray-900 dark:text-white" title={row.dropAddress}>
          {row.dropAddress || '—'}
        </p>
      </div>
    </div>
  );
}

function AmbulanceServiceProvidersCell({
  vendors,
  count,
  onViewAll,
}: {
  vendors: AmbulanceQueueRow['requestedVendors'];
  count: number;
  onViewAll: () => void;
}) {
  if (!count) return <span className="text-gray-400">—</span>;
  const preview = vendors.slice(0, 2).map((v) => {
    const status = v.inviteStatus ? ` (${v.inviteStatus})` : '';
    return `${v.name}${status}`;
  });
  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 dark:text-gray-400">{count} invited</p>
      <p className="text-gray-900 dark:text-white" title={vendors.map((v) => v.name).join(', ')}>
        {preview.join(', ')}
        {vendors.length > 2 ? ` +${vendors.length - 2} more` : ''}
      </p>
      {vendors.length > 0 ? (
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs text-primary-600 hover:underline dark:text-primary-400"
        >
          View all
        </button>
      ) : null}
    </div>
  );
}

function ServiceProvidersCell({
  names,
  pincodes,
  count,
  vendors,
  onViewAll,
}: {
  names: string[];
  pincodes: string[];
  count: number;
  vendors: Array<{ vendorId: string; name: string; kind: string; pincode?: string | null }>;
  onViewAll: () => void;
}) {
  if (!count) return <span className="text-gray-400">—</span>;
  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 dark:text-gray-400">{count} notified</p>
      <p className="text-gray-900 dark:text-white" title={names.join(', ')}>
        {formatVendorNames(names)}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400" title={(pincodes || []).join(', ')}>
        PIN: {formatPinList(pincodes)}
      </p>
      {vendors.length > 0 ? (
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs text-primary-600 hover:underline dark:text-primary-400"
        >
          View all
        </button>
      ) : null}
    </div>
  );
}

function MedicineQueueCard({
  row,
  highlighted,
  onNavigate,
  onViewVendors,
}: {
  row: PrescriptionQueueRow;
  highlighted: boolean;
  onNavigate: (path: string) => void;
  onViewVendors: (vendors: PrescriptionQueueRow['requestedVendors']) => void;
}) {
  return (
    <article
      className={`rounded-lg border p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/80 ${
        highlighted
          ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Prescription</p>
          <p className="font-semibold text-gray-900 dark:text-white" title={row.prescriptionId}>
            {getShortCode(row.prescriptionId)}
          </p>
        </div>
        <StatusBadge status={row.queueStatus} />
      </div>
      {row.queueStatus === 'cancelled' ? (
        <CancellationDetails
          queueStatus={row.queueStatus}
          cancelledBy={formatCancelledByLabel(row.cancelledBy, 'medicine')}
          cancellationReason={row.cancellationReason}
          detailStatus={row.status}
        />
      ) : null}
      <dl className="space-y-2 text-sm">
        <QueueCardField label="User">
          <UserDetailsCell
            userId={row.userId}
            name={row.userName}
            phone={row.userPhone}
            onNavigate={onNavigate}
          />
        </QueueCardField>
        <QueueCardField label="Delivery">
          <LocationCell
            address={row.deliveryAddress}
            pincode={row.deliveryPincode}
            pinLabel="Delivery PIN"
          />
        </QueueCardField>
        <QueueCardField label="Service providers">
          <ServiceProvidersCell
            names={row.requestedVendorNames}
            pincodes={row.requestedVendorPincodes}
            count={row.vendorsNotified}
            vendors={row.requestedVendors}
            onViewAll={() => onViewVendors(row.requestedVendors)}
          />
        </QueueCardField>
        <QueueCardField label="Requested">
          {new Date(row.createdAt).toLocaleString()}
        </QueueCardField>
      </dl>
    </article>
  );
}

function InstantConsultationQueueCard({
  row,
  highlighted,
  onNavigate,
  onViewVendors,
}: {
  row: InstantConsultationQueueRow;
  highlighted: boolean;
  onNavigate: (path: string) => void;
  onViewVendors: (vendors: VendorDialogVendor[]) => void;
}) {
  const shouldShowAmount = shouldShowInstantConsultationAmount(row);
  const displayAmount = shouldShowAmount ? getInstantConsultationDisplayAmount(row) : 0;

  return (
    <article
      className={`rounded-lg border p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/80 ${
        highlighted
          ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Appointment</p>
          <p className="font-semibold text-gray-900 dark:text-white" title={row.clinicAppointmentId}>
            {getShortCode(row.clinicAppointmentId)}
          </p>
        </div>
        <StatusBadge status={row.queueStatus} />
      </div>
      {row.queueStatus === 'cancelled' ? (
        <CancellationDetails
          queueStatus={row.queueStatus}
          cancelledBy={formatCancelledByLabel(row.cancelledBy, 'instant-consultation')}
          cancellationReason={row.cancellationReason}
          detailStatus={row.status}
        />
      ) : null}
      <dl className="space-y-2 text-sm">
        <QueueCardField label="User">
          <UserDetailsCell
            userId={row.userId}
            name={row.userName}
            phone={row.userPhone}
            onNavigate={onNavigate}
          />
        </QueueCardField>
        <QueueCardField label="Speciality">
          {row.selectedSpeciality || '—'}
        </QueueCardField>
        <QueueCardField label="Doctors notified">
          <AmbulanceServiceProvidersCell
            vendors={row.requestedVendors}
            count={row.vendorsNotified}
            onViewAll={() => onViewVendors(row.requestedVendors)}
          />
          {row.acceptedByVendorName ? (
            <p className="mt-1 text-xs text-green-700 dark:text-green-400">
              Accepted by {row.acceptedByVendorName}
            </p>
          ) : null}
        </QueueCardField>
        <QueueCardField label="Payment">
          {row.paymentStatus || '—'}
          {shouldShowAmount ? ` · ₹${displayAmount}` : ''}
        </QueueCardField>
        <QueueCardField label="Requested">
          {new Date(row.createdAt).toLocaleString()}
        </QueueCardField>
      </dl>
    </article>
  );
}

function AmbulanceQueueCard({
  row,
  highlighted,
  onNavigate,
  onViewVendors,
}: {
  row: AmbulanceQueueRow;
  highlighted: boolean;
  onNavigate: (path: string) => void;
  onViewVendors: (vendors: VendorDialogVendor[]) => void;
}) {
  const bookingHref =
    row.acceptedByVendorId
      ? `/vendors/ambulance/${row.acceptedByVendorId}/booking/${row.bookingId}`
      : row.requestedVendors[0]?.vendorId
        ? `/vendors/ambulance/${row.requestedVendors[0].vendorId}/booking/${row.bookingId}`
        : null;

  return (
    <article
      className={`rounded-lg border p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/80 ${
        highlighted
          ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Booking</p>
          {bookingHref ? (
            <button
              type="button"
              onClick={() => onNavigate(bookingHref)}
              className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
              title={row.bookingId}
            >
              {getShortCode(row.bookingId)}
            </button>
          ) : (
            <p className="font-semibold text-gray-900 dark:text-white" title={row.bookingId}>
              {getShortCode(row.bookingId)}
            </p>
          )}
          {row.bookingSource === 'MASTER_ADMIN' ? (
            <span className="mt-1 inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              Manual
            </span>
          ) : null}
        </div>
        <StatusBadge status={row.queueStatus} />
      </div>
      {row.queueStatus === 'cancelled' ? (
        <CancellationDetails
          queueStatus={row.queueStatus}
          cancelledBy={formatCancelledByLabel(row.cancelledBy, 'ambulance')}
          cancellationReason={row.cancellationReason}
          detailStatus={row.bookingStatus}
        />
      ) : null}
      <dl className="space-y-2 text-sm">
        <QueueCardField label="User">
          {row.userId ? (
            <>
              <UserDetailsCell
                userId={row.userId}
                name={row.userName}
                phone={row.userPhone}
                onNavigate={onNavigate}
              />
              {(row.patientName || row.patientPhone) && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Patient: {row.patientName || '—'}
                  {row.patientPhone ? ` · ${row.patientPhone}` : ''}
                </p>
              )}
            </>
          ) : (
            <div>
              <span className="font-medium text-gray-900 dark:text-white">
                {row.patientName || '—'}
              </span>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {row.patientPhone || '—'}
              </div>
            </div>
          )}
        </QueueCardField>
        <QueueCardField label="Route">
          <AmbulanceRouteCell row={row} />
        </QueueCardField>
        <QueueCardField label="Service providers">
          <AmbulanceServiceProvidersCell
            vendors={row.requestedVendors}
            count={row.vendorsNotified}
            onViewAll={() => onViewVendors(row.requestedVendors)}
          />
        </QueueCardField>
        <QueueCardField label="Type">
          {formatAmbulanceType(row.ambulanceType)} ·{' '}
          {row.pickupTimeType === 'SCHEDULED' ? 'Scheduled' : 'Instant'}
        </QueueCardField>
        {row.bookingSource === 'MASTER_ADMIN' && Number(row.totalPayableAmount) > 0 ? (
          <QueueCardField label="Payment">
            ₹{Number(row.totalPayableAmount).toLocaleString('en-IN')}
            {row.paymentStatus ? ` · ${row.paymentStatus}` : ''}
            {row.paymentCollectionType === 'ADVANCE' && Number(row.remainingAmount) > 0
              ? ` · rem. ₹${Number(row.remainingAmount).toLocaleString('en-IN')}`
              : ''}
          </QueueCardField>
        ) : null}
        <QueueCardField label="Requested">
          {new Date(row.createdAt).toLocaleString()}
        </QueueCardField>
        {isAmbulanceOngoing(row) ? (
          <QueueCardField label="Action">
            <button
              type="button"
              onClick={() =>
                onNavigate(`/request-queue/ambulance/${encodeURIComponent(row.bookingId)}/track`)
              }
              className="inline-flex rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400"
            >
              Track on map
            </button>
          </QueueCardField>
        ) : null}
      </dl>
    </article>
  );
}

function QueueCardField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function QueueMobileMessage({
  message,
  loading,
  error,
}: {
  message: string;
  loading?: boolean;
  error?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border border-dashed px-4 py-10 text-center text-sm dark:border-gray-600 ${
        error ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
      }`}
    >
      {loading ? <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-primary-600" /> : null}
      {message}
    </div>
  );
}

function QueueLoadingRow({ colSpan }: { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12">
        <div className="flex flex-col items-center justify-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading queue...</p>
        </div>
      </td>
    </tr>
  );
}

function QueueErrorRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12 text-center text-sm text-red-500 dark:text-red-400">
        {message}
      </td>
    </tr>
  );
}

function QueueEmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        {message}
      </td>
    </tr>
  );
}

export default PrescriptionQueuePage;
